// Resolve a project's commission pool % from its per-project data.
//
// The real per-project commission lives in Postgres `projects.commission`,
// surfaced on the mapped property as `commission` (free text). It is usually
// clean ("5%", "4%", "6%") but is sometimes a slab description, e.g.
//   "DAMAC slabs: 1-2 units 5.5%, 3-4 units 6.0%, 5+ units 7.0%; advance up to 84% upfront"
// The older numeric `commission_percent` / `commissionPercent` (Geniemap) is
// only populated for a small fraction of projects — which is why everything
// used to show the 5% fallback.

const FALLBACK_POOL_PERCENT = 5;

// A commission % is only sane in the 0–50 range — this also rejects the "84%
// upfront advance" and the "1-2 units" slab counts that appear in the free text.
const isSanePercent = (n: number): boolean => Number.isFinite(n) && n > 0 && n <= 50;

/**
 * Extract a usable commission percentage from the per-project `commission`
 * value. Takes the FIRST in-range percentage in the string, so slab text like
 * "1-2 units 5.5%, 3-4 units 6.0%" yields the entry slab (5.5) rather than the
 * "1" or the trailing "84%". Returns null when nothing usable is present.
 */
export function parseCommissionPercent(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') return isSanePercent(raw) ? raw : null;
  const s = String(raw);
  for (const m of s.matchAll(/(\d+(?:\.\d+)?)\s*%/g)) {
    const n = Number(m[1]);
    if (isSanePercent(n)) return n;
  }
  // No "%"-suffixed value — accept a bare numeric string like "5" or "5.5".
  const bare = Number(s.trim());
  return isSanePercent(bare) ? bare : null;
}

/**
 * The project's commission pool %. Priority:
 *   1. numeric `commission_percent` / `commissionPercent` (Geniemap)
 *   2. the per-project Postgres `commission` value (the real per-project number)
 *   3. 5% fallback (only when nothing usable exists)
 */
export function developerPoolPercent(property: unknown): number {
  const p = property as
    | { commission_percent?: number | string; commissionPercent?: number | string; commission?: unknown }
    | null
    | undefined;
  const numeric = Number(p?.commission_percent ?? p?.commissionPercent);
  if (isSanePercent(numeric)) return numeric;
  return parseCommissionPercent(p?.commission) ?? FALLBACK_POOL_PERCENT;
}
